import { el, clear, fmtInt, fmtDate } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { listWaitlist, removeWaitlistEntry } from '../api/waitlist';
import type { WaitlistEntry } from '../api/waitlist';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';

/**
 * The Check ID for Teams launch list.
 *
 * This view exists because a signup used to be invisible: the row was written
 * correctly and there was nowhere to look at it, so a working form and a broken
 * one produced the same experience. Preview volunteers are pulled to the top
 * because they are the ones with something to do about them.
 */
export async function renderWaitlist({ root, query }: RouteContext): Promise<void> {
  const content = renderShell(root, 'waitlist');

  const state = {
    previewOnly: query.get('preview') === '1',
  };

  const filter = el('select', { class: 'control' });
  for (const [value, label] of [
    ['all', 'Everyone'],
    ['preview', 'Preview volunteers only'],
  ] as [string, string][]) {
    const opt = el('option', { value, textContent: label });
    if ((value === 'preview') === state.previewOnly) opt.selected = true;
    filter.append(opt);
  }

  const exportBtn = el('button', { class: 'btn btn-ghost', type: 'button' }, [
    'Export CSV',
  ]);

  const results = el('div', { class: 'results' });
  content.append(
    el('div', { class: 'page-head' }, [el('h1', {}, ['Launch List'])]),
    el('p', { class: 'view-caption' }, [
      'Venues who asked to be told when Check ID for Teams opens. Separate from the Emails view, which is consumer newsletter signups.',
    ]),
    el('div', { class: 'controls' }, [
      el('div', {}, []),
      el('div', { class: 'controls-right' }, [filter, exportBtn]),
    ]),
    results,
  );

  let loaded: WaitlistEntry[] = [];

  function visible(): WaitlistEntry[] {
    const rows = state.previewOnly
      ? loaded.filter((e) => e.wantsPreview)
      : loaded.slice();
    // Volunteers first, then newest. They are the ones there is something to do
    // about, and burying them under a chronological list defeats the point.
    return rows.sort((a, b) => {
      if (a.wantsPreview !== b.wantsPreview) return a.wantsPreview ? -1 : 1;
      return b.createdAt.localeCompare(a.createdAt);
    });
  }

  function syncUrl(): void {
    history.replaceState({}, '', state.previewOnly ? '/waitlist?preview=1' : '/waitlist');
  }

  async function load(): Promise<void> {
    renderLoading(results, 'Loading launch list…');
    syncUrl();
    try {
      loaded = await listWaitlist();
      renderTable(results, visible(), loaded, remove);
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(
        results,
        err instanceof Error ? err.message : 'Failed to load the launch list.',
      );
    }
  }

  filter.addEventListener('change', () => {
    state.previewOnly = filter.value === 'preview';
    renderTable(results, visible(), loaded, remove);
    syncUrl();
  });

  exportBtn.addEventListener('click', () => {
    downloadCsv(visible());
  });

  /**
   * Confirmed, because this is somebody's contact details and there is no undo.
   * Removed from the local list on success rather than refetching, so the table
   * does not flash and lose the reader's place.
   */
  async function remove(entry: WaitlistEntry): Promise<void> {
    if (!window.confirm(`Remove ${entry.email} from the launch list? This cannot be undone.`)) {
      return;
    }
    try {
      await removeWaitlistEntry(entry.id);
      loaded = loaded.filter((e) => e.id !== entry.id);
      renderTable(results, visible(), loaded, remove);
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(results, err instanceof Error ? err.message : 'Failed to remove that entry.');
    }
  }

  await load();
}

function renderTable(
  container: HTMLElement,
  rows: WaitlistEntry[],
  all: WaitlistEntry[],
  onRemove: (entry: WaitlistEntry) => void,
): void {
  clear(container);

  const volunteers = all.filter((e) => e.wantsPreview).length;
  const summary = el('p', {
    class: 'view-caption',
    style: 'margin:0 0 12px;',
    textContent: `${fmtInt(all.length)} on the list · ${fmtInt(volunteers)} volunteered for preview access`,
  });

  if (rows.length === 0) {
    container.append(
      summary,
      el('div', { class: 'empty' }, [
        all.length === 0
          ? 'Nobody has joined the launch list yet.'
          : 'No preview volunteers yet.',
      ]),
    );
    return;
  }

  const body = rows.map((e) =>
    el('tr', {}, [
      el('td', { class: 'cell-strong', textContent: e.email }),
      el('td', {}, [
        e.wantsPreview
          ? el('span', { class: 'pill pill-ok', textContent: 'Preview' })
          : el('span', { class: 'cell-muted', textContent: '—' }),
      ]),
      el('td', { textContent: e.organizationName || '—' }),
      el('td', {
        class: 'cell-muted',
        textContent: [e.region, e.country].filter(Boolean).join(', ') || '—',
      }),
      el('td', {
        class: 'cell-muted',
        textContent: e.venueCount === null ? '—' : String(e.venueCount),
      }),
      el('td', { class: 'cell-muted', textContent: e.source || '—' }),
      el('td', { class: 'cell-muted', textContent: e.note || '—' }),
      el('td', { class: 'cell-muted', textContent: fmtDate(e.createdAt) }),
      el('td', {}, [
        el(
          'button',
          {
            class: 'btn btn-ghost',
            type: 'button',
            title: `Remove ${e.email}`,
            onClick: () => onRemove(e),
          },
          ['Remove'],
        ),
      ]),
    ]),
  );

  container.append(
    summary,
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'data-table' }, [
        el('thead', {}, [
          el(
            'tr',
            {},
            ['Email', 'Preview', 'Venue', 'Region', 'Locations', 'Source', 'Note', 'Joined', ''].map(
              (h) => el('th', { textContent: h }),
            ),
          ),
        ]),
        el('tbody', {}, body),
      ]),
    ]),
  );
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function downloadCsv(rows: WaitlistEntry[]): void {
  const lines = [
    'Email,Wants Preview,Venue,Region,Country,Locations,Source,Note,Joined',
  ];
  for (const e of rows) {
    lines.push(
      [
        csvEscape(e.email),
        e.wantsPreview ? 'yes' : 'no',
        csvEscape(e.organizationName ?? ''),
        csvEscape(e.region ?? ''),
        csvEscape(e.country ?? ''),
        e.venueCount === null ? '' : String(e.venueCount),
        csvEscape(e.source ?? ''),
        csvEscape(e.note ?? ''),
        csvEscape(e.createdAt),
      ].join(','),
    );
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `teams_launch_list_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
